import React, { useEffect, useState } from 'react';
import { Archive, CreditCard, Keyboard, LogOut, Settings, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAdminStatus, useSignOut } from '../../hooks/queries/useAuth';
import { useProjectUIStore } from '../../store/projectUIStore';
import { logger } from '../../utils/logger';
import { Dropdown, DropdownContent, DropdownItem } from '../ui';
import { useViewport } from '../../contexts/ViewportContext';

interface ProfileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement>;
}

export function ProfileMenu({ isOpen, onClose, triggerRef }: ProfileMenuProps) {
  const navigate = useNavigate();
  const { mutateAsync: signOut } = useSignOut();
  const { refetch: refetchAdminStatus } = useAdminStatus();
  const { showArchived, setShowArchived } = useProjectUIStore();
  const [isAdmin, setIsAdmin] = useState(false);
  const { isMobile } = useViewport();

  // Empty trigger since the button is handled by the parent component
  const emptyTrigger = <span style={{ display: 'none' }} />;

  useEffect(() => {
    // Check admin status when the menu opens
    if (isOpen) {
      logger.component('ProfileMenu: Checking admin status', { menuOpen: isOpen });
      refetchAdminStatus()
        .then(result => {
          const status = result.data || false;
          logger.component('ProfileMenu: Admin status result', { isAdmin: status });
          setIsAdmin(status);
        })
        .catch(err => {
          logger.error('ProfileMenu: Error checking admin status', {
            error: err instanceof Error ? err.message : String(err),
          });
          setIsAdmin(false);
        });
    }
  }, [isOpen, refetchAdminStatus]);

  const handleEditProfile = () => {
    navigate('/profile');
    onClose();
  };

  const handleArchived = () => {
    setShowArchived(!showArchived);
    onClose();
  };

  const handleSignOut = async () => {
    await signOut();
    onClose();
  };

  const handleAdminAccess = async () => {
    logger.component('ProfileMenu: Navigating to admin page', { isAdmin });
    // Force an admin check before navigating
    const result = await refetchAdminStatus();
    const confirmed = result.data || false;
    logger.component('ProfileMenu: Pre-navigation admin check', { confirmed });

    // Set a session flag to indicate admin status
    sessionStorage.setItem('admin_verified', 'true');

    // Then navigate
    navigate('/admin');
    onClose();
  };

  // Make sure we don't render anything when closed
  if (!isOpen) return null;

  return (
    <Dropdown
      trigger={emptyTrigger}
      open={true}
      defaultOpen={true}
      onOpenChange={open => {
        // If dropdown wants to close, tell parent to close
        if (!open) onClose();
      }}
      width={224} // w-56 in pixels
      isMobile={isMobile}
      position="top-right" // Position above the footer for desktop
      offset={{ x: 0, y: -10 }} // Add some offset to position it away from the button
      showBackdrop={isMobile}
      mobilePosition={0.2} // Position near the top of the screen
      anchorElement={triggerRef?.current || null}
      disablePositionOptimization={isMobile} // Only optimize position on desktop
      contentClassName="border-0" // Explicitly remove border
    >
      {isMobile ? (
        <>
          {/* Custom header with title for mobile */}
          {/* <div className="px-4 py-3 bg-gray-900 border-b border-gray-800 flex justify-between items-center">
            <h2 className="text-lg font-medium text-white">Profile Menu</h2>
          </div>*/}
          <DropdownContent className="pt-0">
            <DropdownItem leftIcon={<Settings size={18} />} onClick={handleEditProfile}>
              Edit Profile
            </DropdownItem>

            <DropdownItem leftIcon={<Archive size={18} />} onClick={handleArchived}>
              {showArchived ? 'Active projects' : 'Archived projects'}
            </DropdownItem>

            <DropdownItem
              leftIcon={<Keyboard size={18} />}
              onClick={() => {
                if (window.toggleKeyboardShortcutsHelp) {
                  window.toggleKeyboardShortcutsHelp();
                }
                onClose();
              }}
            >
              Keyboard Shortcuts
            </DropdownItem>

            {isAdmin && (
              <DropdownItem leftIcon={<ShieldCheck size={18} />} onClick={handleAdminAccess}>
                Admin
              </DropdownItem>
            )}

            <DropdownItem
              leftIcon={<CreditCard size={18} />}
              onClick={() => {
                navigate('/subscription');
                onClose();
              }}
            >
              Subscription
            </DropdownItem>

            <DropdownItem leftIcon={<LogOut size={18} />} onClick={handleSignOut}>
              Sign Out
            </DropdownItem>
          </DropdownContent>
        </>
      ) : (
        <DropdownContent>
          <DropdownItem leftIcon={<Settings size={16} />} onClick={handleEditProfile}>
            Edit Profile
          </DropdownItem>

          <DropdownItem leftIcon={<Archive size={16} />} onClick={handleArchived}>
            {showArchived ? 'Active projects' : 'Archived projects'}
          </DropdownItem>

          <DropdownItem
            leftIcon={<Keyboard size={16} />}
            onClick={() => {
              if (window.toggleKeyboardShortcutsHelp) {
                window.toggleKeyboardShortcutsHelp();
              }
              onClose();
            }}
          >
            Keyboard Shortcuts
          </DropdownItem>

          {isAdmin && (
            <DropdownItem leftIcon={<ShieldCheck size={16} />} onClick={handleAdminAccess}>
              Admin
            </DropdownItem>
          )}

          <DropdownItem
            leftIcon={<CreditCard size={16} />}
            onClick={() => {
              navigate('/subscription');
              onClose();
            }}
          >
            Subscription
          </DropdownItem>

          <DropdownItem leftIcon={<LogOut size={16} />} onClick={handleSignOut}>
            Sign Out
          </DropdownItem>
        </DropdownContent>
      )}
    </Dropdown>
  );
}
